import { useEffect, useRef, useState, memo } from 'react';
import { StyledCell, StyledCellInput, StyledCellValue } from './styles';
import { formatCurrency, formatPercentage } from '../../../../utils/strings';
import normalizeNumber from '../../../../utils/number';

interface CartTableCellProps {
    value: string;
    onSubmit?: (newValue: number) => void;
    editable?: boolean;
    loading?: boolean;
    format?: 'currency' | 'percent';
    maxValue?: number;
}

function CartTableCell({
    value,
    editable = false,
    onSubmit,
    loading,
    format = 'currency',
    maxValue,
}: CartTableCellProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string>(value);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!editing) setDraft(value);
    }, [value, editing]);

    useEffect(() => {
        if (editing) inputRef.current?.select();
    }, [editing]);

    const handleConfirm = () => {
        const parsed = normalizeNumber(draft);

        if (
            !Number.isNaN(parsed) &&
            parsed !== parseFloat(value) &&
            (!maxValue || maxValue >= parsed)
        )
            onSubmit(parsed);

        setEditing(false);
    };

    const handleCancel = () => {
        setDraft(value);
        setEditing(false);
    };

    const isInvalidNumber = (v: unknown) => !Number.isFinite(Number(v));

    if (loading || isInvalidNumber(value)) return <>Calculando...</>;

    return (
        <StyledCell
            $editable={editable}
            onClick={() => editable && setEditing(true)}
        >
            <StyledCellValue $isEditing={editing}>
                {format === 'currency'
                    ? formatCurrency(parseFloat(value ?? '0'))
                    : formatPercentage(value ?? '0')}
            </StyledCellValue>

            <StyledCellInput
                $isEditing={editing}
                type="text"
                inputMode="decimal"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={handleConfirm}
                onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleConfirm();
                    }

                    if (e.key === 'Escape') {
                        e.stopPropagation();
                        handleCancel();
                    }
                }}
                ref={inputRef}
            />
        </StyledCell>
    );
}

export default memo(CartTableCell, (prevProps, nextProps) => {
    return (
        prevProps.value === nextProps.value &&
        prevProps.loading === nextProps.loading &&
        prevProps.editable === nextProps.editable &&
        prevProps.format === nextProps.format &&
        prevProps.maxValue === nextProps.maxValue
    );
});
